<template>
  <div v-if="project" class="d-flex hover-container py-2">
    <card-user-avatar
      :large="true"
      :fullname="teamMember.user ? (teamMember.user.firstname + ' ' + teamMember.user.lastname) : null"
      :picture="teamMember.user?.picture"
    ></card-user-avatar>

    <div class="pt-1 ml-2">
      <p v-if="teamMember.user" class="ma-0" :class="{'font-weight-bold': isProjectOwner(teamMember, project)}">
        {{ teamMember.user.firstname }} {{ teamMember.user.lastname }}
        <v-tooltip v-if="isProjectOwner(teamMember, project)" location="bottom">
          <template v-slot:activator="{ props: props }">
            <span v-bind="props"> (Owner)</span>
          </template>
          <span>The owner of this project.</span>
        </v-tooltip>
        <v-tooltip v-if="isLoggedInUser(teamMember)" location="bottom">
          <template v-slot:activator="{ props: props }">
            <span class="font-weight-bold" v-bind="props"> (You)</span>
          </template>
          <span>This logged in user.</span>
        </v-tooltip>
      </p>
      <p v-else class="ma-0">
        {{ teamMember.email }}
      </p>
    </div>

    <v-spacer/>
    <div v-if="!$store.state.isWorkflowDisabled" class="show-on-hover">
      <v-menu>
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon="mds:more_horiz"
            variant="plain"
          />
        </template>
        <v-list>
          <v-tooltip
            location="bottom"
            :disabled="canRemoveTeamMember"
          >
            <template v-slot:activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-list-item
                  :disabled="!canRemoveTeamMember"
                  @click="showDeleteTeamMemberDialog"
                >
                  <v-list-item-title>
                    {{ isLoggedInUser(teamMember) ? 'Leave project' : 'Remove' }}
                    <span v-if="isProjectOwner(teamMember, project) && !isLoggedInUser(teamMember)">(cannot remove owner)</span>
                  </v-list-item-title>
                </v-list-item>
              </div>
            </template>

            <span>{{ removeTeamMemberTooltip }}</span>
          </v-tooltip>
        </v-list>
      </v-menu>
    </div>

    <delete-confirmation-dialog
      v-model="deleteDialog.show"
      :title="deleteDialog.title"
      :text="deleteDialog.text"
      :onDelete="deleteDialog.action"
    />
  </div>
</template>

<script>
import isToday from 'date-fns/isToday'
import isYesterday from 'date-fns/isYesterday'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import projectApi from '@/api/projectApi'
import CardUserAvatar from '@/components/CardUserAvatar.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'
import { mapState } from 'vuex'

export default {
  components: {
    CardUserAvatar,
    DeleteConfirmationDialog
  },
  props: {
    teamMember: Object,
    project: Object
  },

  data () {
    return {
      deleteDialog: {
        show: false,
        title: 'Confirm team member deletion',
        text: 'Are you sure you want to remove the team member?',
        action: null
      }
    }
  },

  computed: {
    ...mapState({
      currentUser: state => state.user.user
    }),

    ownsProject () {
      return this.currentUser?.id === this.project?.organisation?.userId
    },

    canRemoveTeamMember () {
      if (this.isProjectOwner(this.teamMember, this.project)) {
        return false
      }

      return this.ownsProject || this.isLoggedInUser(this.teamMember)
    },

    removeTeamMemberTooltip () {
      if (this.isProjectOwner(this.teamMember, this.project)) {
        return this.isLoggedInUser(this.teamMember)
          ? 'Assign another team member as project owner before leaving the project.'
          : 'The project owner cannot be removed from the project.'
      }

      return 'Only the project owner can remove other members.'
    }
  },

  methods: {
    isToday,
    isYesterday,
    formatDistanceToNow,
    deleteTeamMember: projectApi.deleteTeamMember,
    isProjectOwner: (teamMember, project) => teamMember.userId === project?.organisation?.userId,
    isLoggedInUser (teamMember) {
      return teamMember.userId === this.currentUser?.id
    },

    showDeleteTeamMemberDialog () {
      const isSelf = this.isLoggedInUser(this.teamMember)
      this.deleteDialog = {
        show: true,
        title: isSelf ? 'Confirm leaving the project' : 'Confirm team member deletion',
        text: isSelf
          ? 'You are about to remove yourself from this project and will not be able to access its workflows anymore.'
          : 'Are you sure you want to remove the team member?',
        action: () => this.deleteTeamMember(this.$router, this.teamMember, this.project.id)
      }
    }
  }
}
</script>

<style scoped>
.online-status {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
}

.online-status.online {
  background: #c6f4b0;
}

.online-status.recent {
  background: #ffea86;
}

.online-status.offline {
  background: #f16e81;
}
</style>
